import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Home, Mail, Shield, ArrowRight, Lock, User, Sparkles } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmailCapture() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [loading, setLoading] = useState(false);

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async () => {
    if (!firstName.trim()) {
      toast.error("Please enter your first name");
      return;
    }
    
    if (!email.trim() || !isValidEmail(email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    if (!consent) {
      toast.error("Please accept the privacy policy to continue");
      return;
    }

    setLoading(true);
    
    try {
      await axios.post(`${API}/cipher/email`, {
        session_id: sessionId,
        email: email.trim(),
        first_name: firstName.trim(),
        marketing_consent: marketingConsent
      });
      
      navigate(`/insight/${sessionId}`);
    } catch (error) {
      console.error("Email capture error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-cipher-bone to-white flex flex-col">
      {/* Header */}
      <header className="cipher-container pt-6 pb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-cipher-green flex items-center justify-center">
            <Home className="w-4 h-4 text-white" />
          </div>
          <span className="font-heading font-bold text-lg text-cipher-charcoal">HiPPO</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col justify-center cipher-container pb-12">
        <div className="animate-fade-up max-w-sm mx-auto w-full">
          {/* Icon */}
          <div className="w-20 h-20 rounded-2xl bg-cipher-green/10 flex items-center justify-center mx-auto mb-6">
            <Sparkles className="w-10 h-10 text-cipher-green" strokeWidth={1.5} />
          </div>

          {/* Heading */}
          <div className="text-center mb-8">
            <h1 className="font-poppins font-semibold text-2xl sm:text-3xl text-cipher-charcoal mb-3">
              Your insight is ready
            </h1>
            <p className="text-gray-500 text-sm sm:text-base">
              Enter your details to reveal your personalised HiPPO insight
            </p>
          </div>

          {/* Form Fields */}
          <div className="space-y-4 mb-6">
            {/* Name Input */}
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Your first name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="cipher-input pl-11"
                data-testid="name-input"
              />
            </div>
            
            {/* Email Input */}
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="email"
                placeholder="Your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="cipher-input pl-11"
                autoFocus
                data-testid="email-input"
              />
            </div>
          </div>

          {/* Submit Button - Before consent */}
          <Button
            onClick={handleSubmit}
            className="cipher-btn-primary w-full mb-6"
            disabled={loading || !firstName.trim() || !email.trim() || !consent}
            data-testid="reveal-insight-btn"
          >
            {loading ? (
              <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                Reveal My Insight
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>

          {/* Consent Checkboxes - After button */}
          <div className="space-y-4 mb-6">
            {/* Required privacy consent */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="privacy-consent"
                checked={consent}
                onCheckedChange={setConsent}
                className="mt-0.5 border-2 data-[state=checked]:bg-cipher-green data-[state=checked]:border-cipher-green"
                data-testid="privacy-consent-checkbox"
              />
              <label htmlFor="privacy-consent" className="text-sm text-gray-600 cursor-pointer">
                I agree to the{" "}
                <a href="#" className="text-cipher-green underline hover:text-cipher-green-dark">
                  Privacy Policy
                </a>{" "}
                and{" "}
                <a href="#" className="text-cipher-green underline hover:text-cipher-green-dark">
                  Terms of Service
                </a>
                <span className="text-red-500 ml-1">*</span>
              </label>
            </div>

            {/* Optional marketing consent */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="marketing-consent"
                checked={marketingConsent}
                onCheckedChange={setMarketingConsent}
                className="mt-0.5 border-2 data-[state=checked]:bg-cipher-green data-[state=checked]:border-cipher-green"
                data-testid="marketing-consent-checkbox"
              />
              <label htmlFor="marketing-consent" className="text-sm text-gray-600 cursor-pointer">
                Send me property tips and market updates (optional)
              </label>
            </div>
          </div>

          {/* Security Info - Only padlock */}
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-center gap-3 text-sm text-gray-600">
              <Lock className="w-5 h-5 text-cipher-green flex-shrink-0" />
              <span>Your data is protected and never shared</span>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="cipher-container py-4">
        <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
          <Shield className="w-3 h-3" />
          <span>Your data is encrypted and secure</span>
        </div>
      </footer>
    </div>
  );
}
